/**
 * The soft-tissue springs in the viewport — ADR-038 J2: **one declared spring
 * per grafted leaf, the Body tab's region controls, and the reference solver
 * that plays them over whatever clip runs.** The tail spring's sibling
 * (`viewport/tail-spring.ts`), kept apart so neither control can adopt the
 * other's spring.
 *
 * WHERE A SPRING COMES FROM, per region:
 *   1. a leaf this session GREW (`run.softTissueGrafts`) — the region prior's
 *      declaration (`softTissueSpring`), colliders fitted from this body at
 *      bind (the upper arms and thighs measured from their own flesh);
 *   2. a file that DECLARES one (`jiggle-<Region>` in VRMC_springBone, its
 *      surface in `extras.poqpoq.jiggle`) — adopted as written, never
 *      re-derived; the controls retune its joints.
 *
 * WHAT IS LIVE (J3). Every leaf's runtime is LAYERED over a drive with no
 * mood and no muscle (`softTissueDrive`): the animation is the target, so a
 * clip that keys the leaf is followed and physics plays around it; max effect
 * is the cone; gravity falloff gates gravity by displacement; bounce and sway
 * scale the shown deviation about the leaf's measured lateral and up axes.
 * Mass is in the joint (stiffness / mass). Spread alone is RECORDED —
 * `SOFT_TISSUE_LIVE_CONTROLS` is the list.
 *
 * THE SLOT. Like the tail's, each spring rides the viewport's render-preview
 * slot: applied before a frame, restored after, so export, package and the
 * pose tool always read the leaves at rest. The runtime is the tail's
 * (`createSpringRuntime`, fixed 60 Hz, held pose).
 *
 * Depends on: three, adapter-three (runtime, declared soft tissue, subject
 * arrays), kernel (the spring prior, limb radii). Depended on by: main (the
 * render slot, the export door, the Body tab), the specimen probe.
 */
import { Vector3, type Object3D } from 'three';
import {
  createSpringRuntime,
  declaredSoftTissue,
  measureDriveAxes,
  subjectArrays,
  type DriveAxes,
  type SpringRuntime,
  type SpringRuntimeDrive,
} from '@harryhausen/adapter-three';
import {
  kindOfRegion,
  limbFleshRadius,
  retuneSoftTissueSpring,
  softTissueExtras,
  softTissueRegionOfSpring,
  softTissueSpring,
  SOFT_TISSUE_DEFAULTS,
  SOFT_TISSUE_LIVE_CONTROLS,
  softTissueDynamics,
  type RigDescriptor,
  type SoftTissueColliderFit,
  type SoftTissueControls,
  type SoftTissueExtras,
  type SoftTissueLeafInput,
  type JiggleRegionId,
  type SpringDeclaration,
} from '@harryhausen/kernel';
import type { SoftTissueGraftRecord } from './soft-tissue-run.js';

/** What the controller needs of a run. */
export interface SoftTissueSpringRun {
  readonly scene: Object3D;
  readonly rig: RigDescriptor;
  readonly softTissueGrafts: readonly SoftTissueGraftRecord[];
}

export type SoftTissueSpringSource = 'grown' | 'file';

export interface SoftTissueSpringView {
  readonly region: JiggleRegionId;
  readonly source: SoftTissueSpringSource;
  readonly on: boolean;
  readonly controls: SoftTissueControls;
  readonly live: readonly (keyof SoftTissueControls)[];
  /** The leaf as the file names it. */
  readonly bone: string;
  readonly colliders: readonly { readonly bone: string; readonly kind: string; readonly radiusCm: number; readonly note: string }[];
}

export interface SoftTissueSpringProbe {
  readonly region: JiggleRegionId;
  readonly on: boolean;
  readonly source: SoftTissueSpringSource;
  readonly steps: number;
  readonly colliders: number;
}

interface Entry {
  region: JiggleRegionId;
  source: SoftTissueSpringSource;
  base: SpringDeclaration;
  fitted: readonly SoftTissueColliderFit[] | null;
  controls: SoftTissueControls;
  touched: boolean;
  runtime: SpringRuntime | null;
  /** The leaf's drive axes, measured at bind once (the runtime's first frame). */
  axes: DriveAxes[] | null;
  /** When the last feedback kick landed (performance clock, seconds). */
  kickedAt: number;
}

const fileName = (n: string): string => n.replace(/_graft$/, '');

/**
 * THE FEEDBACK KICK (Allen, 2026-09-27): a control that moves shows itself —
 * each change drops the mass with an upward speed of `KICK_PER_S` leaf lengths
 * per second, at most once per `KICK_EVERY_S` while a slider drags.
 */
const KICK_PER_S = 8;
/** The share of Max effect over which a swing is eased rather than stopped (the runtime's `softCone`). */
export const SOFT_TISSUE_SOFT_CONE = 0.4;
const KICK_EVERY_S = 0.35;

/**
 * The leaf's layered drive (ADR-038 J3): no mood (the target is the animated
 * pose as it stands) and no muscle (gain 0 — physics alone returns the mass),
 * the region's cone and falloff, and bounce / sway on the axes measured at
 * bind — bounce about the leaf's lateral axis (the tip rises and falls), sway
 * about the body's up (the tip swings sideways).
 */
export function softTissueDrive(axes: readonly DriveAxes[], controls: SoftTissueControls): SpringRuntimeDrive {
  return {
    sample: () => undefined,
    dynamics: softTissueDynamics(controls),
    reflex: false,
    response: {
      axes: axes.slice(0, -1).map((a) => ({ bounce: a.pitch, sway: a.yaw })),
      bounce: controls.bounce,
      sway: controls.sway,
    },
    gravityFalloff: controls.gravityFalloff,
    // The mass eases into Max effect over its last 40% and springs back off it.
    softCone: SOFT_TISSUE_SOFT_CONE,
  };
}

/**
 * The prior's springs for every leaf a run grew, colliders fitted at BIND:
 * the limb radii come from the flesh (bind-world positions, the current
 * paint), the positions from the rig's world bind — nothing is posed.
 */
export function grownSoftTissueSprings(
  run: SoftTissueSpringRun,
  controlsOf: (region: JiggleRegionId) => SoftTissueControls | undefined = () => undefined,
): { region: JiggleRegionId; decl: SpringDeclaration; extras: SoftTissueExtras; fitted: readonly SoftTissueColliderFit[] }[] {
  if (run.softTissueGrafts.length === 0) return [];
  const arrays = subjectArrays(run.scene);
  const skin = arrays.skin;
  const lowerOf = (upper: string): string | undefined => {
    const id = run.rig.mapping.find((m) => m.rigBone === upper)?.ontologyId;
    const next = id === 'LeftArm' ? 'LeftForeArm' : id === 'RightArm' ? 'RightForeArm' : id === 'LeftUpLeg' ? 'LeftLeg' : id === 'RightUpLeg' ? 'RightLeg' : undefined;
    return next === undefined ? undefined : run.rig.mapping.find((m) => m.ontologyId === next)?.rigBone;
  };
  const radii = new Map<string, number | undefined>();
  const limbRadius = (upper: string): number | undefined => {
    if (!radii.has(upper)) {
      const lower = lowerOf(upper);
      radii.set(upper, skin === null || lower === undefined ? undefined : limbFleshRadius({ positions: arrays.positions, skin, bones: arrays.bones, upper, lower }));
    }
    return radii.get(upper);
  };
  const leaves: SoftTissueLeafInput[] = run.softTissueGrafts.map((g) => ({ region: g.region, bone: g.bone, tip: g.tip, head: g.head, tail: g.tail, radius: g.radius }));
  return leaves.map((leaf) => {
    const s = softTissueSpring({ leaf, others: leaves, rig: run.rig, limbRadius, ...(controlsOf(leaf.region) !== undefined ? { controls: controlsOf(leaf.region)! } : {}), fileName });
    return { region: leaf.region, decl: s.decl, extras: s.extras, fitted: s.fitted };
  });
}

export interface SoftTissueSprings {
  views(run: SoftTissueSpringRun): readonly SoftTissueSpringView[];
  setOn(run: SoftTissueSpringRun, region: JiggleRegionId, on: boolean): void;
  setControls(run: SoftTissueSpringRun, region: JiggleRegionId, controls: Partial<SoftTissueControls>, confirmedBy: string): void;
  /** Put every leaf back at rest and forget its velocity (a clip change, the settle button). */
  reset(run: SoftTissueSpringRun): void;
  playing(run: SoftTissueSpringRun): boolean;
  /** This frame's springs over the live leaves, or null — handed to the render-preview slot. */
  applyFrame(run: SoftTissueSpringRun, frameDt: number): (() => void) | null;
  /** What the export writes: the declarations of the springs that are on… */
  springsForExport(run: SoftTissueSpringRun): readonly SpringDeclaration[];
  /** …and their region surfaces, by spring name (`exportGlb`'s `springExtras`). */
  extrasForExport(run: SoftTissueSpringRun): Readonly<Record<string, Readonly<Record<string, unknown>>>>;
  probe(run: SoftTissueSpringRun): readonly SoftTissueSpringProbe[];
  /** The leaf bone objects the running solvers write (empty before their first frame). */
  springBones(run: SoftTissueSpringRun): readonly Object3D[];
}

export function createSoftTissueSprings(): SoftTissueSprings {
  const entries = new WeakMap<Object3D, Map<JiggleRegionId, Entry>>();

  const entriesOf = (run: SoftTissueSpringRun): Map<JiggleRegionId, Entry> => {
    const cached = entries.get(run.scene);
    if (cached !== undefined) return cached;
    const made = new Map<JiggleRegionId, Entry>();
    for (const g of grownSoftTissueSprings(run)) {
      made.set(g.region, { region: g.region, source: 'grown', base: g.decl, fitted: g.fitted, controls: g.extras.controls, touched: false, runtime: null, axes: null, kickedAt: -Infinity });
    }
    for (const d of declaredSoftTissue(run.scene)) {
      const region = d.extras?.region ?? softTissueRegionOfSpring(d.decl);
      if (region === null || made.has(region)) continue;
      const kind = kindOfRegion(region);
      const knobs = d.decl.provenance.knobs;
      const controls = d.extras?.controls ?? { ...SOFT_TISSUE_DEFAULTS[kind], stiffness: knobs.stiffness, drag: knobs.drag, gravity: knobs.gravity };
      made.set(region, { region, source: 'file', base: d.decl, fitted: null, controls, touched: false, runtime: null, axes: null, kickedAt: -Infinity });
    }
    entries.set(run.scene, made);
    return made;
  };
  const declarationOf = (e: Entry): SpringDeclaration =>
    e.touched ? retuneSoftTissueSpring(e.base, e.controls, e.base.provenance.confirmedBy ?? 'human:local') : e.base;

  const api: SoftTissueSprings = {
    views(run) {
      return [...entriesOf(run).values()].map((e) => {
        const d = declarationOf(e);
        return {
          region: e.region,
          source: e.source,
          on: e.controls.on,
          controls: e.controls,
          live: SOFT_TISSUE_LIVE_CONTROLS,
          bone: d.joints[0]!.bone,
          colliders:
            e.fitted !== null
              ? e.fitted.map((f) => ({
                  bone: f.bone,
                  kind: f.kind,
                  radiusCm: +(f.radius * 100).toFixed(1),
                  note: `${f.what}${f.dropped ? ' — dropped, the tip starts too close' : f.clipped ? ` — shrunk from ${(f.fleshRadius * 100).toFixed(1)} cm to clear the tip` : ''}`,
                }))
              : d.colliders.map((c) => ({
                  bone: c.bone,
                  kind: 'sphere' in c.shape ? 'sphere' : 'capsule',
                  radiusCm: +(('sphere' in c.shape ? c.shape.sphere.radius : c.shape.capsule.radius) * 100).toFixed(1),
                  note: 'as the file declares',
                })),
        };
      });
    },
    setOn(run, region, on) {
      const e = entriesOf(run).get(region);
      if (e === undefined) return;
      e.controls = { ...e.controls, on };
      e.runtime?.reset();
    },
    setControls(run, region, controls, confirmedBy) {
      const e = entriesOf(run).get(region);
      if (e === undefined) return;
      e.controls = { ...e.controls, ...controls };
      e.touched = true;
      e.base = { ...e.base, provenance: { ...e.base.provenance, confirmedBy } };
      e.runtime?.retune(declarationOf(e));
      if (e.runtime !== null && e.axes !== null) e.runtime.setDrive(softTissueDrive(e.axes, e.controls));
      const now = performance.now() / 1000;
      if (e.runtime !== null && e.controls.on && now - e.kickedAt >= KICK_EVERY_S) {
        e.kickedAt = now;
        const b = e.runtime.bones;
        const len = b[0]!.getWorldPosition(new Vector3()).distanceTo(b[b.length - 1]!.getWorldPosition(new Vector3()));
        e.runtime.kick({ x: 0, y: KICK_PER_S * len, z: 0 });
      }
    },
    reset(run) {
      for (const e of entriesOf(run).values()) e.runtime?.reset();
    },
    playing(run) {
      return [...entriesOf(run).values()].some((e) => e.controls.on);
    },
    applyFrame(run, frameDt) {
      const restores: (() => void)[] = [];
      for (const e of entriesOf(run).values()) {
        if (!e.controls.on) continue;
        if (e.runtime === null) {
          e.runtime = createSpringRuntime(run.scene, declarationOf(e));
          e.axes = measureDriveAxes(run.scene, e.runtime.bones);
          e.runtime.setDrive(softTissueDrive(e.axes, e.controls));
        }
        restores.push(e.runtime.hold());
        e.runtime.advance(Math.min(Math.max(frameDt, 0), 0.25));
      }
      if (restores.length === 0) return null;
      return () => {
        for (let k = restores.length - 1; k >= 0; k--) restores[k]!();
      };
    },
    springsForExport(run) {
      return [...entriesOf(run).values()].filter((e) => e.controls.on).map(declarationOf);
    },
    extrasForExport(run) {
      const out: Record<string, Record<string, unknown>> = {};
      for (const e of entriesOf(run).values()) {
        if (!e.controls.on) continue;
        out[declarationOf(e).name] = { jiggle: softTissueExtras(e.region, e.controls) };
      }
      return out;
    },
    probe(run) {
      return [...entriesOf(run).values()].map((e) => ({
        region: e.region,
        on: e.controls.on,
        source: e.source,
        steps: e.runtime?.probe().steps ?? 0,
        colliders: declarationOf(e).colliders.length,
      }));
    },
    springBones(run) {
      return [...entriesOf(run).values()].flatMap((e) => (e.runtime === null ? [] : [e.runtime.bones[0]!]));
    },
  };
  return api;
}

/** The file names a region's leaf wears (for the card and the e2e). */
export const leafFileName = fileName;
